import React, { useCallback } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { GetEventCTAButtonsDataQuery } from '../CtaButtons/CtaButtons.graphql.generated';
import { buildRsvpPath, performFullPageRedirect } from '@shared/utils/navigation';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { pxToRem } from '@withjoy/joykit/theme';
import { useAttendanceStatusConfig } from './attendanceStatusConfig';
import { useGuestGuideTelemetry } from './GuestGuideTelemetry';
import { useRsvpDeadlineGuard } from '../../common/utils/useRsvpDeadlineGuard';
import useQueryString from '@shared/utils/hooks/useQueryString';

type RsvpStatusSectionProps = Readonly<{
  sessionData: GetEventCTAButtonsDataQuery | null | undefined;
}>;

export const RsvpStatusSection: React.FC<RsvpStatusSectionProps> = ({ sessionData }) => {
  const { tq } = useTranslation('guestGuide');
  const telemetry = useGuestGuideTelemetry();
  const { eventName: eventHandle } = useEventNameMatch();
  const queryString = useQueryString();

  const guestName = sessionData?.eventByName?.activeSession.person?.firstName ?? '';
  const rsvpAttendanceStatus = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus;
  const statusConfig = useAttendanceStatusConfig(rsvpAttendanceStatus, 'mono14');

  const { isRsvpClosedByDeadline } = useRsvpDeadlineGuard();

  const onRsvpHandle = useCallback(() => {
    if (!eventHandle || isRsvpClosedByDeadline) {
      return;
    }
    telemetry.clickEditRsvp();
    performFullPageRedirect(buildRsvpPath(eventHandle, queryString));
  }, [eventHandle, telemetry, isRsvpClosedByDeadline, queryString]);

  // Only show if we have a guest name
  if (!guestName) {
    return null;
  }

  return (
    <Box
      marginBottom={6}
      marginTop={3}
    >
      <TextV2
        typographyVariant={'hed5'}
        marginBottom={4}
      >
        {tq`Hi ${guestName}!`}
      </TextV2>
      {/* */}
      {statusConfig && (
        <Flex
          alignItems="center"
          gap={4}
        >
          <Flex
            height="32px"
            paddingX={4}
            borderRadius="full"
            backgroundColor="mono2"
            alignItems="center"
            justifyContent="center"
            gap={3}
          >
            {statusConfig.icon}
            <TextV2
              fontSize="label3"
              fontWeight={600}
              lineHeight={1.4}
              color="mono14"
            >
              {statusConfig.label}
            </TextV2>
          </Flex>
          {!isRsvpClosedByDeadline && (
            <ButtonV2
              variant="ghost"
              intent="neutral"
              color="mono8"
              lineHeight={1.5}
              typographyVariant="label3"
              height={pxToRem(20)}
              padding={0}
              borderBottomLeftRadius={0}
              borderBottomRightRadius={0}
              borderBottom="1px solid"
              borderBottomColor="mono5"
              onClick={onRsvpHandle}
            >
              {tq`Change Response`}
            </ButtonV2>
          )}
        </Flex>
      )}
    </Box>
  );
};
